import { unstable_useId as useId } from "reakit";
import type { ComponentPropsWithoutRef, MouseEvent } from "react";
import { forwardRef, useContext, useMemo } from "react";
import { ArrowRight } from "@mos-cat/ds-icons";
import { pxToRem } from "@mos-cat/ds-theme";
import { useRegisterRow } from "../internal/useRegisterRow";
import { Box } from "../../Box/Box";
import { Row } from "../internal/Row";
import type { OptionParams } from "../internal/types";
import { Inline } from "../../Inline/Inline";
import { UtilityButton } from "../../UtilityButton/UtilityButton";
import { searchContext } from "../internal/context";
import { WHCM } from "../../constants";
import { createLanguageGetter } from "../internal/text";

type SubmitOptionProps = Omit<ComponentPropsWithoutRef<"div">, "role" | "children">;

export const SubmitOption = forwardRef<HTMLDivElement, SubmitOptionProps>(function SubmitOption(
  props,
  ref
) {
  const { id } = useId(props) as { id: string };
  const cellIds = useMemo<OptionParams["cellIds"]>(
    function getCellIds() {
      return [`${id}-content`];
    },
    [id]
  );
  const { send, lang } = useContext(searchContext);
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  useRegisterRow({
    type: "submit",
    id,
    value: "",
    cellIds,
  });
  return (
    <Row
      ref={ref}
      {...props}
      id={id}
      pl={["sp-12", pxToRem(46)]}
      onMouseUpCapture={(event: MouseEvent<HTMLDivElement>) => {
        props.onMouseUp?.(event);
        if (event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        send("submit");
      }}
    >
      <Box role="gridcell">
        <UtilityButton
          styles={{
            textAlign: "left",
            color: "borderlessBtnText",
            ":hover, :active": { color: "borderlessBtnText" },
            [WHCM]: {
              borderColor: "transparent",
            },
          }}
          id={cellIds[0]}
          type="button"
          onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
        >
          <Inline space="sp-8" alignItems="center">
            <span>{getText("toSearchResults")}</span>
            <Box as={ArrowRight} size="1.25rem" display={["none", "block"]} />
          </Inline>
        </UtilityButton>
      </Box>
    </Row>
  );
});

SubmitOption.displayName = "SubmitOption";
